import React, { useState } from "react";
import { Droppable } from "react-beautiful-dnd";

import { Box, Typography } from "@mui/material";
import { TableExpandProvider } from "components/Report-generator/context/TableExpandContext";
import type {
  CanvasCard,
  CardConfig,
  PageLayout,
  ReportMeta,
  ReportOrientation,
  ReportPage,
  SizePreset
} from "store/types";

import { CardWrapper } from "../CardWrapper";
import { ReportHeader } from "./ReportHeader";
import { SectionBreakHeader } from "./SectionBreakHeader";
import { TitlePageContent } from "./TitlePageContent";

const EMPTY_SET: Set<string> = new Set();

export const PAGE_LAYOUT_OPTIONS: { value: PageLayout; label: string; tag: string }[] = [
  { value: "title", label: "Title Page", tag: "TP" },
  { value: "header-content", label: "Header + Content", tag: "HC" },
  { value: "content", label: "Content Only", tag: "CO" },
  { value: "section", label: "Section Break", tag: "SB" }
];

interface PageViewProps {
  pageIndex: number;
  pageCount: number;
  page: ReportPage;
  pageCards: CanvasCard[];
  allCards: CanvasCard[];
  pageWidth: number;
  pageHeight: number;
  selectedCardId: string;
  reportMeta: ReportMeta;
  orientation: ReportOrientation;
  isResizing: boolean;
  droppablePageIndex?: number;
  /** When set, marks this page as a continuation (overflow) page with a unique droppable suffix. */
  continuationChunkIndex?: number;
  /** When true, hides layout picker and remove-page button. */
  isContinuation?: boolean;
  expandedRows?: Record<string, Set<string>>;
  onToggleExpand?: (cardId: string, rowKey: string) => void;
  cardOffsets?: Record<string, Record<string, number>>;
  originalCardIds?: Record<string, string>;
  onResizingChange: (resizing: boolean) => void;
  onUpdateMeta: (updates: Partial<ReportMeta>) => void;
  onLogoFileChange: (file: File | null) => void;
  onSelectCard: (id: string) => void;
  onRemoveCard: (id: string) => void;
  onDuplicateCard: (id: string) => void;
  onUpdateCard: (id: string, updates: Partial<Omit<CanvasCard, "id" | "type">>) => void;
  onRemovePage: () => void;
  onUpdatePage: (updates: Partial<ReportPage>) => void;
}

export const PageView: React.FC<PageViewProps> = ({
  pageIndex,
  pageCount,
  page,
  pageCards,
  allCards: _allCards,
  pageWidth,
  pageHeight,
  selectedCardId,
  reportMeta,
  orientation,
  isResizing,
  droppablePageIndex,
  continuationChunkIndex,
  isContinuation: isContinuationPage,
  expandedRows,
  onToggleExpand,
  cardOffsets,
  originalCardIds,
  onResizingChange,
  onUpdateMeta,
  onLogoFileChange,
  onSelectCard,
  onRemoveCard,
  onDuplicateCard,
  onUpdateCard,
  onRemovePage,
  onUpdatePage
}) => {
  const [showLayoutPicker, setShowLayoutPicker] = useState(false);
  const baseDroppableId = `canvas-page-${droppablePageIndex ?? pageIndex}`;
  const droppableId =
    continuationChunkIndex != null ? `${baseDroppableId}-cont-${continuationChunkIndex}` : baseDroppableId;

  const layout = page.layout;
  const showHeader = layout === "header-content";
  const isTitlePage = layout === "title";
  const isSectionBreak = layout === "section";
  return (
    <Box sx={{ position: "relative" }}>
      {/* Page badge + layout picker */}
      <Box
        sx={{
          position: "absolute",
          top: -22,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "center",
          gap: "6px"
        }}
      >
        <Box
          sx={{
            bgcolor: "#071f35",
            color: "#FFFFFF",
            fontSize: 10,
            fontWeight: 800,
            p: "3px 12px",
            borderRadius: "999px",
            letterSpacing: "0.04em"
          }}
        >
          PAGE {pageIndex + 1} OF {pageCount}
        </Box>
        {!isContinuationPage && (
          <Box
            onClick={() => setShowLayoutPicker((v) => !v)}
            sx={{
              bgcolor: showLayoutPicker ? "#071f35" : "rgba(7,31,53,0.08)",
              color: showLayoutPicker ? "#FFFFFF" : "#071f35",
              border: "none",
              borderRadius: "999px",
              fontSize: 10,
              fontWeight: 700,
              p: "3px 12px",
              cursor: "pointer"
            }}
          >
            {PAGE_LAYOUT_OPTIONS.find((o) => o.value === layout)?.label ?? layout}
          </Box>
        )}
      </Box>

      {showLayoutPicker && !isContinuationPage && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: "50%",
            transform: "translateX(-50%)",
            bgcolor: "#FFFFFF",
            borderRadius: "10px",
            boxShadow: "0 12px 32px rgba(0,0,0,0.16)",
            border: "1px solid #dbe8f5",
            p: 1,
            display: "flex",
            gap: "6px",
            zIndex: 20,
            flexWrap: "wrap",
            justifyContent: "center",
            width: Math.min(pageWidth - 32, 540)
          }}
        >
          {PAGE_LAYOUT_OPTIONS.map((option) => (
            <Box
              key={option.value}
              component="button"
              onClick={() => {
                onUpdatePage({ layout: option.value });
                setShowLayoutPicker(false);
              }}
              sx={{
                p: "8px 12px",
                border: `1px solid ${option.value === layout ? "#071f35" : "#dbe8f5"}`,
                borderRadius: "8px",
                bgcolor: option.value === layout ? "#E8F5F0" : "#FAFCFE",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "4px",
                minWidth: 94
              }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#071f35" }}>{option.tag}</Typography>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#071f35" }}>{option.label}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {pageCards.length === 0 && pageIndex > 0 && !isContinuationPage && (
        <Box
          component="button"
          onClick={onRemovePage}
          sx={{
            position: "absolute",
            top: -14,
            right: 0,
            zIndex: 5,
            bgcolor: "#FEE2E2",
            color: "#d93e3e",
            border: "none",
            borderRadius: "999px",
            fontSize: 10,
            fontWeight: 700,
            p: "4px 10px",
            cursor: "pointer"
          }}
        >
          Remove page
        </Box>
      )}

      <Box
        sx={{
          width: pageWidth,
          height: pageHeight,
          flexShrink: 0,
          bgcolor: "#FFFFFF",
          boxShadow: "0 4px 32px rgba(0,0,0,0.18), 0 1px 6px rgba(0,0,0,0.10)",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          ...(isTitlePage || isSectionBreak
            ? {}
            : {
                backgroundImage: [
                  "linear-gradient(rgba(7,31,53,0.05) 1px, transparent 1px)",
                  "linear-gradient(90deg, rgba(7,31,53,0.05) 1px, transparent 1px)"
                ].join(", "),
                backgroundSize: `${pageWidth / 3}px 76px`
              })
        }}
        onClick={() => {
          onSelectCard("");
          setShowLayoutPicker(false);
        }}
      >
        {isTitlePage && (
          <TitlePageContent
            meta={reportMeta}
            pageHeight={pageHeight}
            onUpdate={onUpdateMeta}
            onLogoFileChange={onLogoFileChange}
          />
        )}
        {isSectionBreak && (
          <SectionBreakHeader title={page.title ?? ""} onTitleChange={(title) => onUpdatePage({ title })} />
        )}
        {showHeader && <ReportHeader meta={reportMeta} onUpdate={onUpdateMeta} onLogoFileChange={onLogoFileChange} />}

        {!isTitlePage && (
          <Droppable droppableId={droppableId}>
            {(provided, snapshot) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "14px",
                  p: pageCards.length === 0 ? 0 : "34px 28px 16px 60px",
                  flex: 1,
                  minHeight: 0,
                  transition: "background 0.15s",
                  ...(pageCards.length === 0
                    ? { bgcolor: snapshot.isDraggingOver ? "rgba(7,31,53,0.04)" : "transparent" }
                    : {})
                }}
              >
                {pageCards.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: 1,
                      gap: "12px",
                      border: `2px dashed ${snapshot.isDraggingOver ? "#071f35" : "rgba(7,31,53,0.2)"}`,
                      borderRadius: "10px",
                      m: "28px",
                      boxSizing: "border-box"
                    }}
                  >
                    <svg width="32" height="40" viewBox="0 0 32 40" fill="none">
                      <rect
                        x="1"
                        y="1"
                        width="30"
                        height="38"
                        rx="2"
                        stroke={snapshot.isDraggingOver ? "#071f35" : "rgba(7,31,53,0.28)"}
                        strokeWidth="1.5"
                      />
                      <line
                        x1="7"
                        y1="12"
                        x2="25"
                        y2="12"
                        stroke={snapshot.isDraggingOver ? "#071f35" : "rgba(7,31,53,0.28)"}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                      <line
                        x1="7"
                        y1="18"
                        x2="25"
                        y2="18"
                        stroke={snapshot.isDraggingOver ? "#071f35" : "rgba(7,31,53,0.28)"}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                      <line
                        x1="7"
                        y1="24"
                        x2="18"
                        y2="24"
                        stroke={snapshot.isDraggingOver ? "#071f35" : "rgba(7,31,53,0.28)"}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                    <Typography
                      sx={{ fontSize: 15, fontWeight: 700, color: snapshot.isDraggingOver ? "#071f35" : "#92a7bc" }}
                    >
                      {snapshot.isDraggingOver ? "Drop to add content" : "Drag components here"}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#92a7bc" }}>
                      {PAGE_LAYOUT_OPTIONS.find((o) => o.value === layout)?.label ?? "Page"} - Page {pageIndex + 1}
                    </Typography>
                  </Box>
                ) : (
                  (() => {
                    const rows: CanvasCard[][] = [];
                    let currentRow: CanvasCard[] = [];
                    let rowWidth = 0;
                    for (const card of pageCards) {
                      if (rowWidth > 0 && rowWidth + card.widthPercent > 100.01) {
                        rows.push([...currentRow]);
                        currentRow = [];
                        rowWidth = 0;
                      }
                      currentRow.push(card);
                      rowWidth += card.widthPercent;
                    }
                    if (currentRow.length > 0) rows.push(currentRow);

                    const canvasContentWidth = pageWidth - 56;
                    let flatIndex = 0;
                    return rows.map((rowCards, rowIdx) => (
                      <Box key={rowIdx} sx={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
                        {rowCards.map((card) => {
                          const idx = flatIndex++;
                          const origId = originalCardIds?.[card.id] ?? card.id;
                          const isContinuationCard = originalCardIds?.[card.id] !== undefined;
                          const expandValue =
                            expandedRows && onToggleExpand
                              ? {
                                  expanded: expandedRows[origId] ?? EMPTY_SET,
                                  onToggle: (rowKey: string) => onToggleExpand(origId, rowKey),
                                  indexOffsets: cardOffsets?.[card.id]
                                }
                              : null;
                          return (
                            <TableExpandProvider key={card.id} value={expandValue}>
                              <CardWrapper
                                card={card}
                                index={idx}
                                isSelected={selectedCardId === origId}
                                onSelect={() => onSelectCard(origId)}
                                onRemove={() => onRemoveCard(origId)}
                                onDuplicate={() => onDuplicateCard(origId)}
                                onUpdateConfig={(config: CardConfig) => onUpdateCard(origId, { config })}
                                onWidthChange={(widthPercent: number, preset: SizePreset | null) =>
                                  onUpdateCard(origId, {
                                    widthPercent,
                                    ...(preset !== null ? { lastPreset: preset } : {})
                                  })
                                }
                                onHeightChange={(heightPx) => onUpdateCard(origId, { heightPx })}
                                onTopOffsetChange={(topOffsetPx) => onUpdateCard(origId, { topOffsetPx })}
                                onLeftOffsetChange={(leftOffsetPx) => onUpdateCard(origId, { leftOffsetPx })}
                                readOnly={isContinuationCard}
                                canvasWidthPx={canvasContentWidth}
                                rowCards={rowCards}
                                isResizing={isResizing}
                                onResizingChange={onResizingChange}
                                orientation={orientation}
                              />
                            </TableExpandProvider>
                          );
                        })}
                      </Box>
                    ));
                  })()
                )}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        )}

        {isTitlePage && (
          <Droppable droppableId={droppableId}>
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 1 }}
              >
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        )}

        {/* Footer */}
        <Box
          sx={{
            borderTop: "1px solid #dbe8f5",
            p: "12px 28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            flexShrink: 0,
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0
          }}
        >
          <Typography sx={{ fontSize: 10, color: "#92a7bc" }}>
            Page {pageIndex + 1} of {pageCount}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
